import React, { useState, useEffect, useCallback, useRef } from "react";
import { Save, ShieldCheck, Zap, Globe, Copy, Trash2, Link2, CheckCircle2, Loader2, ChevronRight, ChevronDown, Sparkles } from "lucide-react";
import ApiClient from "../utils/api";
import { useToast } from "../context/ToastContext";
import PageHeader from "../components/PageHeader";
import DeleteConfirmModal from "../components/DeleteConfirmModal";

const UNCHANGED_KEY = "__UNCHANGED__";
const MASK_DISPLAY = "••••••••••••••••••••";

const CustomDropdown = ({
  value,
  onChange,
  options,
  placeholder,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedOption = options.find((opt) => opt.value === value);

  return (
    <div ref={dropdownRef} className="relative">
      <div
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`w-full px-5 py-4 border border-gray-100 rounded-2xl text-sm bg-gray-50 ${disabled
          ? "opacity-50 cursor-not-allowed"
          : "cursor-pointer hover:bg-gray-100/50"
          } flex justify-between items-center transition-all`}
      >
        <span className={selectedOption ? "text-gray-900 font-medium" : "text-gray-400"}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown
          size={16}
          className={`transition-transform text-gray-600 ${isOpen ? "rotate-180" : ""}`}
        />
      </div>

      {isOpen && !disabled && (
        <div className="absolute top-full left-0 right-0 bg-white border border-gray-100 rounded-2xl mt-1 max-h-60 overflow-y-auto z-50 shadow-xl shadow-gray-100/30">
          {options.map((option) => (
            <div
              key={option.value}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
              className={`px-5 py-3.5 text-sm cursor-pointer hover:bg-gray-50 transition-all ${value === option.value
                ? "bg-gray-50 text-gray-900 font-bold"
                : "text-gray-700 font-medium"
                }`}
            >
              {option.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const WhatsAppConfig = ({ activeAiAgentId }) => {
  const { showToast } = useToast();
  const [whatsappConfig, setWhatsappConfig] = useState({
    access_token: "",
    phone_number_id: "",
    waba_id: "",
    verify_token: "",
    communication_mode: "bidirectional",
    is_active: false
  });
  const [webhookUrl, setWebhookUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [dbConfig, setDbConfig] = useState(null);

  // ─── Embedded Signup State ─────────────────────────────────────────────────
  const [embeddedConfig, setEmbeddedConfig] = useState(null); // { app_id, config_id }
  const [signupLoading, setSignupLoading] = useState(false);
  const [signupStatus, setSignupStatus] = useState(null); // 'success' | 'error' | null
  const pendingSignupData = useRef(null); // holds { code, waba_id, phone_number_id }

  // Load Meta App ID + Config ID from backend (keeps App Secret server-side)
  useEffect(() => {
    ApiClient.request("/whatsapp/embedded-signup/app-config", { aiAgentId: activeAiAgentId })
      .then((res) => { if (res.success) setEmbeddedConfig(res); })
      .catch(() => { /* server not configured — hide the section */ });
  }, [activeAiAgentId]);

  // Inject Facebook JS SDK once
  useEffect(() => {
    if (!embeddedConfig || document.getElementById("fb-jssdk")) return;
    window.fbAsyncInit = function () {
      window.FB.init({ appId: embeddedConfig.app_id, autoLogAppEvents: true, xfbml: true, version: "v25.0" });
    };
    const script = document.createElement("script");
    script.id = "fb-jssdk";
    script.src = "https://connect.facebook.net/en_US/sdk.js";
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }, [embeddedConfig]);

  // Listen for postMessage from Facebook popup — captures waba_id + phone_number_id
  // NOTE: 'code' is NOT in this message. It comes from response.authResponse.code in FB.login callback.
  useEffect(() => {
    const handleMessage = (event) => {
      if (
        event.origin !== "https://www.facebook.com" &&
        event.origin !== "https://web.facebook.com"
      ) return;

      try {
        const data = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
        console.log("[WA Embedded Signup] postMessage received:", data);
        if (data?.type === "WA_EMBEDDED_SIGNUP") {
          // Only process FINISH events — CANCEL events don't have waba_id/phone_number_id
          if (data.event !== "FINISH") {
            console.log("[WA Embedded Signup] Skipping non-FINISH event:", data.event);
            return;
          }
          // code is NOT here — only waba_id and phone_number_id are from the postMessage
          const sessionData = data.data || data;
          const waba_id = sessionData?.waba_id;
          const phone_number_id = sessionData?.phone_number_id;
          console.log("[WA Embedded Signup] Session info (FINISH):", { waba_id, phone_number_id });
          if (waba_id && phone_number_id) {
            // Merge into pendingSignupData (code will be added by the FB.login callback)
            pendingSignupData.current = {
              ...(pendingSignupData.current || {}),
              waba_id,
              phone_number_id,
            };
          }
        }
      } catch (_) { /* not our message */ }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  const launchWhatsAppSignup = () => {
    if (!embeddedConfig || !window.FB) {
      showToast("Facebook SDK not ready yet. Please wait a moment and try again.", "error");
      return;
    }
    pendingSignupData.current = null;
    setSignupStatus(null);
    setSignupLoading(true);

    window.FB.login(
      (response) => {
        console.log("[WA Embedded Signup] FB.login response:", response);
        if (response.authResponse) {
          // code comes from response.authResponse.code — NOT from postMessage
          const code = response.authResponse.code;
          console.log("[WA Embedded Signup] Auth code received:", code);

          // Store the code immediately into pendingSignupData
          pendingSignupData.current = {
            ...(pendingSignupData.current || {}),
            code,
          };

          (async () => {
            // Wait for the WA_EMBEDDED_SIGNUP postMessage (waba_id + phone_number_id)
            // Retry up to 10 times with 300ms intervals (max 3 seconds wait)
            let signup = null;
            for (let i = 0; i < 10; i++) {
              await new Promise((r) => setTimeout(r, 300));
              const current = pendingSignupData.current;
              if (current?.code && current?.waba_id && current?.phone_number_id) {
                signup = current;
                break;
              }
            }

            if (!signup?.code || !signup?.waba_id || !signup?.phone_number_id) {
              console.error("[WA Embedded Signup] Missing data after waiting:", pendingSignupData.current);
              showToast("Signup completed but WABA/Phone data was missing. Please retry.", "error");
              setSignupStatus("error");
              setSignupLoading(false);
              return;
            }

            console.log("[WA Embedded Signup] Sending to backend:", signup);
            try {
              const res = await ApiClient.request("/whatsapp/embedded-signup", {
                aiAgentId: activeAiAgentId,
                method: "POST",
                body: JSON.stringify(signup),
              });
              if (res.success) {
                showToast("WhatsApp connected successfully! 🎉", "success");
                setSignupStatus("success");
                await loadConfig();
                setWebhookUrl(res.webhook_url || "");
              } else {
                throw new Error(res.detail || "Unknown error");
              }
            } catch (err) {
              showToast("Failed to complete WhatsApp setup: " + (err?.message || "Try again."), "error");
              setSignupStatus("error");
            } finally {
              setSignupLoading(false);
            }
          })();
        } else {
          showToast("WhatsApp signup was cancelled.", "info");
          setSignupStatus(null);
          setSignupLoading(false);
        }
      },
      {
        config_id: embeddedConfig.config_id,
        response_type: "code",
        override_default_response_type: true,
        extras: {
          setup: {},
          sessionInfoVersion: 2, // REQUIRED: without this, WA_EMBEDDED_SIGNUP postMessage is never fired
        },
      }
    );
  };

  const loadConfig = useCallback(async () => {
    try {
      const response = await ApiClient.request("/whatsapp/config", { aiAgentId: activeAiAgentId });
      if (response.success && response.config) {
        setWhatsappConfig({
          access_token: response.config.access_token ? UNCHANGED_KEY : "",
          phone_number_id: response.config.phone_number_id || "",
          waba_id: response.config.waba_id || "",
          verify_token: response.config.verify_token ? UNCHANGED_KEY : "",
          communication_mode: response.config.communication_mode || "bidirectional",
          is_active: response.config.is_active || false
        });
        setWebhookUrl(response.config.webhook_url || "");
        setDbConfig(response.config);
      } else {
        setDbConfig(null);
      }
    } catch (error) {
      console.log("WhatsApp config not found");
      setDbConfig(null);
    }
  }, [activeAiAgentId]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const saveConfig = async () => {
    if (!whatsappConfig.access_token.trim()) {
      showToast("Please enter your Access Token", "error");
      return;
    }
    if (!whatsappConfig.phone_number_id.trim()) {
      showToast("Please enter your Phone Number ID", "error");
      return;
    }

    try {
      setSaving(true);
      const response = await ApiClient.request("/whatsapp/config", {
        aiAgentId: activeAiAgentId,
        method: "PUT",
        body: JSON.stringify(whatsappConfig),
      });

      if (response.success) {
        setWebhookUrl(response.webhook_url);
        showToast("WhatsApp configuration saved successfully!", "success");
        await loadConfig();
      }
    } catch (error) {
      // Error handled by ApiClient global toast
    } finally {
      setSaving(false);
    }
  };

  const deleteConfig = async () => {
    try {
      setSaving(true);
      const response = await ApiClient.request("/ai-agents/integrations/whatsapp", {
        method: "DELETE"
      });
      if (response.success) {
        setWhatsappConfig({
          access_token: "",
          phone_number_id: "",
          waba_id: "",
          verify_token: "",
          communication_mode: "bidirectional",
          is_active: false
        });
        setWebhookUrl("");
        setDbConfig(null);
        showToast("WhatsApp integration deleted successfully!", "success");
      }
    } catch (error) {
      // handled globally
    } finally {
      setSaving(false);
      setIsDeleteModalOpen(false);
    }
  };

  const getDisplayWebhookUrl = () => {
    if (!webhookUrl) return "";
    let path = webhookUrl;
    if (webhookUrl.startsWith('http://') || webhookUrl.startsWith('https://')) {
      try {
        const urlObj = new URL(webhookUrl);
        path = urlObj.pathname + urlObj.search;
      } catch (e) { }
    }
    const baseUrl = process.env.REACT_APP_API_BASE_URL || window.location.origin;
    let cleanBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    let cleanPath = path.startsWith('/') ? path : '/' + path;
    if (cleanBase.endsWith('/api') && cleanPath.startsWith('/api')) {
      cleanBase = cleanBase.slice(0, -4);
    }
    return cleanBase + cleanPath;
  };

  const displayUrl = getDisplayWebhookUrl();

  const copyWebhookUrl = async () => {
    try {
      await navigator.clipboard.writeText(displayUrl);
      setCopied(true);
      showToast("Webhook URL copied to clipboard!", "success");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      showToast("Failed to copy URL", "error");
    }
  };

  const toggleActive = async () => {
    if (!whatsappConfig.is_active) {
      if (!whatsappConfig.access_token?.trim() || !whatsappConfig.phone_number_id?.trim()) {
        showToast("Please complete and save your WhatsApp config first", "error");
        return;
      }

      const response = await ApiClient.request("/whatsapp/config", {
        aiAgentId: activeAiAgentId,
        method: "PUT",
        body: JSON.stringify({
          ...whatsappConfig,
          is_active: true
        }),
      });

      if (response.success) {
        setWhatsappConfig((prev) => ({ ...prev, is_active: true }));
        showToast("WhatsApp integration activated!", "success");
      }
    } else {
      const response = await ApiClient.request("/whatsapp/config", {
        aiAgentId: activeAiAgentId,
        method: "PUT",
        body: JSON.stringify({
          ...whatsappConfig,
          is_active: false
        }),
      });

      if (response.success) {
        setWhatsappConfig((prev) => ({ ...prev, is_active: false }));
        showToast("WhatsApp integration deactivated", "info");
      }
    }
  };

  const isConnected = !!(dbConfig && dbConfig.access_token);

  return (
    <div className="min-h-screen bg-[#FDFCFD] pt-4 pb-8 px-8 lg:pt-6 lg:pb-12 lg:px-12 animate-fadeIn flex justify-center items-start border-t border-gray-100">
      <div className="w-full max-w-4xl space-y-8">
        <PageHeader
          title={
            <div className="relative flex items-center justify-center min-h-[44px] w-full">
              <span>WhatsApp Bridge</span>
            </div>
          }
          description="Deploy your AI agent to chat with customers on WhatsApp."
          actions={
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-3 bg-white px-5 py-3 rounded-[24px] border border-gray-100 shadow-xl shadow-gray-100/50">
                <span className={`text-[10px] font-black uppercase tracking-[0.2em] ${whatsappConfig.is_active ? "text-[#83bbe1]" : "text-gray-300"}`}>
                  {whatsappConfig.is_active ? "Operational" : "Offline"}
                </span>

                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={whatsappConfig.is_active}
                    onChange={toggleActive}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-white border-2 border-gray-200 rounded-full peer peer-checked:border-[#83bbe1] transition-all duration-300 after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-gray-300 after:rounded-full after:h-4 after:w-4 after:transition-all duration-300 peer-checked:after:translate-x-5 peer-checked:after:bg-[#83bbe1]"></div>
                </label>
              </div>
            </div>
          }
        />

        <div className="grid gap-8 animate-slideIn">
          {/* ─── Embedded Signup Card ─── */}
          <div className="relative bg-white rounded-[32px] border border-gray-100 shadow-xl shadow-gray-100/50 overflow-hidden">
            <div className="p-8">
              <div className="flex items-start justify-between gap-6">
                {/* Left — icon + text */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#25d366] to-[#128c7e] flex items-center justify-center shadow-lg shadow-green-100 flex-shrink-0">
                    <svg viewBox="0 0 24 24" fill="white" width="22" height="22">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                      <path d="M12 0C5.373 0 0 5.373 0 12c0 2.124.558 4.119 1.533 5.85L.057 23.428a.5.5 0 00.609.61l5.697-1.498A11.95 11.95 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.94 0-3.763-.522-5.33-1.432l-.385-.226-3.98 1.048 1.054-3.88-.236-.394A9.956 9.956 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/>
                    </svg>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-black text-gray-900 text-sm uppercase tracking-widest">Quick Connect</h3>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-50 border border-gray-100 text-[9px] font-bold text-gray-500 uppercase tracking-wider">
                        <Sparkles size={9} />
                        Recommended
                      </span>
                    </div>
                    <p className="text-[12px] font-medium text-gray-400 leading-relaxed max-w-sm">
                      One-click WhatsApp business setup.
                    </p>
                  </div>
                </div>

                {/* Right — button */}
                <div className="flex-shrink-0 flex flex-col items-end gap-2">
                  <button
                    onClick={launchWhatsAppSignup}
                    disabled={signupLoading || isConnected}
                    className={`flex items-center gap-2.5 px-6 py-3 rounded-2xl font-bold text-sm transition-all duration-200 shadow-lg ${
                      isConnected
                        ? "bg-gray-900 text-white shadow-none cursor-default"
                        : signupLoading
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed shadow-none"
                        : "bg-gray-900 text-white hover:bg-gray-800 active:scale-95"
                    }`}
                  >
                    {signupLoading ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : isConnected ? (
                      <CheckCircle2 size={16} />
                    ) : (
                      <Link2 size={16} />
                    )}
                    {signupLoading
                      ? "Connecting..."
                      : isConnected
                      ? "Connected!"
                      : "Connect with WhatsApp"}
                    {!signupLoading && !isConnected && (
                      <ChevronRight size={14} className="opacity-70" />
                    )}
                  </button>
                  {signupStatus === "error" && (
                    <p className="text-[10px] font-bold text-red-500">Connection failed — try again</p>
                  )}
                  
                </div>
              </div>

              {/* Divider + help text */}
              <div className="mt-6 pt-5 border-t border-gray-50 flex items-center gap-3">
                <div className="flex-1 h-px bg-gray-100" />
                <span className="text-[10px] font-bold text-gray-300 uppercase tracking-widest whitespace-nowrap">or enter credentials manually</span>
                <div className="flex-1 h-px bg-gray-100" />
              </div>
            </div>
          </div>

          {/* Credentials Card */}
          <div className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-xl shadow-gray-100/50 relative overflow-hidden">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 bg-gray-900 rounded-xl flex items-center justify-center text-white">
                <ShieldCheck size={20} />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 text-xs uppercase tracking-widest">Credentials</h3>
                <p className="text-[11px] font-medium text-gray-400">Securely connect your WhatsApp App</p>
              </div>
            </div>

            <div className="space-y-6">
              <div>
                <label className="block mb-2 text-sm font-bold text-gray-700 ml-1">
                  Access Token
                </label>
                <div className="relative group">
                  <input
                    type="password"
                    value={whatsappConfig.access_token === UNCHANGED_KEY ? MASK_DISPLAY : whatsappConfig.access_token}
                    onChange={(e) =>
                      setWhatsappConfig({
                        ...whatsappConfig,
                        access_token: e.target.value,
                      })
                    }
                    onFocus={() => {
                      if (whatsappConfig.access_token === UNCHANGED_KEY) {
                        setWhatsappConfig({ ...whatsappConfig, access_token: "" });
                      }
                    }}
                    placeholder={whatsappConfig.access_token === UNCHANGED_KEY ? MASK_DISPLAY : "Enter your WhatsApp Access Token"}
                    className="w-full px-5 py-4 bg-gray-50 border-0 rounded-2xl focus:ring-2 focus:ring-gray-900 text-gray-900 placeholder:text-gray-400 text-sm transition-all"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none opacity-0 group-focus-within:opacity-100 transition-opacity">
                    <Zap size={16} className="text-gray-400" />
                  </div>
                </div>
              </div>

              <div>
                <label className="block mb-2 text-sm font-bold text-gray-700 ml-1">
                  Phone Number ID
                </label>
                <input
                  type="text"
                  value={whatsappConfig.phone_number_id}
                  onChange={(e) =>
                    setWhatsappConfig({
                      ...whatsappConfig,
                      phone_number_id: e.target.value,
                    })
                  }
                  placeholder="Enter your WhatsApp Phone Number ID"
                  className="w-full px-5 py-4 bg-gray-50 border-0 rounded-2xl focus:ring-2 focus:ring-gray-900 text-gray-900 placeholder:text-gray-400 text-sm transition-all"
                />
              </div>

              <div>
                <label className="block mb-2 text-sm font-bold text-gray-700 ml-1">
                  WhatsApp Business Account ID <span className="text-[10px] font-medium text-gray-400 ml-1">(WABA ID)</span>
                </label>
                <input
                  type="text"
                  value={whatsappConfig.waba_id}
                  onChange={(e) =>
                    setWhatsappConfig({
                      ...whatsappConfig,
                      waba_id: e.target.value,
                    })
                  }
                  placeholder="Enter your WhatsApp Business Account ID"
                  className="w-full px-5 py-4 bg-gray-50 border-0 rounded-2xl focus:ring-2 focus:ring-gray-900 text-gray-900 placeholder:text-gray-400 text-sm transition-all"
                />

              </div>

              <div>
                <label className="block mb-2 text-sm font-bold text-gray-700 ml-1">
                  Verify Token
                </label>
                <input
                  type="password"
                  value={whatsappConfig.verify_token === UNCHANGED_KEY ? MASK_DISPLAY : whatsappConfig.verify_token}
                  onChange={(e) =>
                    setWhatsappConfig({
                      ...whatsappConfig,
                      verify_token: e.target.value,
                    })
                  }
                  onFocus={() => {
                    if (whatsappConfig.verify_token === UNCHANGED_KEY) {
                      setWhatsappConfig({ ...whatsappConfig, verify_token: "" });
                    }
                  }}
                  placeholder={whatsappConfig.verify_token === UNCHANGED_KEY ? MASK_DISPLAY : "Enter your Webhook Verification Token"}
                  className="w-full px-5 py-4 bg-gray-50 border-0 rounded-2xl focus:ring-2 focus:ring-gray-900 text-gray-900 placeholder:text-gray-400 text-sm transition-all"
                />
              </div>

              <div>
                <label className="block mb-2 text-sm font-bold text-gray-700 ml-1">
                  Way of Communication
                </label>
                <CustomDropdown
                  value={whatsappConfig.communication_mode || "bidirectional"}
                  onChange={(val) =>
                    setWhatsappConfig({
                      ...whatsappConfig,
                      communication_mode: val,
                    })
                  }
                  options={[
                    { value: "bidirectional", label: "Bidirectional" },
                    { value: "unidirectional", label: "Unidirectional" }
                  ]}
                  placeholder="Select way of communication"
                />
              </div>


              <div className="pt-4 border-t border-gray-50 flex justify-between items-center">
                {whatsappConfig.phone_number_id && (
                  <button
                    onClick={() => setIsDeleteModalOpen(true)}
                    disabled={saving}
                    className="flex items-center gap-2 px-6 py-3 border border-red-200 text-red-600 rounded-2xl hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-bold text-sm"
                  >
                    <Trash2 size={18} />
                    Delete Integration
                  </button>
                )}
                <button
                  onClick={saveConfig}
                  disabled={saving || !whatsappConfig.access_token?.trim() || !whatsappConfig.phone_number_id?.trim()}
                  className="flex items-center gap-2 px-8 py-3.5 bg-gray-900 text-white rounded-2xl hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-bold shadow-lg shadow-gray-200 text-sm ml-auto"
                >
                  <Save size={18} />
                  {saving ? "Saving Changes..." : "Save Settings"}
                </button>
              </div>
            </div>
          </div>

          {/* Webhook Card */}
          {displayUrl && (
            <div className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-xl shadow-gray-100/50 animate-slideUp">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center text-blue-600">
                  <Globe size={20} />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-xs uppercase tracking-widest">Webhook Endpoint</h3>
                  <p className="text-[11px] font-medium text-gray-400">Real-time message delivery endpoint</p>
                </div>
              </div>

              <div className="p-6 bg-gray-50 rounded-[24px] border-2 border-dashed border-gray-200 group transition-all hover:border-blue-200 hover:bg-blue-50/10">
                <label className="block mb-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                  Live Webhook URL
                </label>
                <div className="flex items-center justify-between gap-4">
                  <div className="font-mono text-xs text-gray-600 truncate flex-1 leading-loose">
                    {displayUrl}
                  </div>
                  <button
                    onClick={copyWebhookUrl}
                    className="flex items-center gap-2 px-4 py-2 bg-white hover:bg-gray-900 hover:text-white rounded-xl text-[10px] font-bold text-gray-500 transition-all border border-gray-100 shadow-sm"
                  >
                    <Copy size={12} />
                    {copied ? "Copied!" : "Copy URL"}
                  </button>
                </div>
              </div>
              <p className="mt-4 text-xs text-gray-400">
                Copy this URL and paste it into the <span className="font-bold">Webhook</span> section of your Facebook Developer App configuration.
              </p>
            </div>
          )}
        </div>
      </div>

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={deleteConfig}
        title="Delete WhatsApp Integration"
        message="Are you sure you want to delete this integration config? This will remove all credentials and disable the webhook."
        confirmText="Delete Integration"
        isLoading={saving}
      />
    </div>
  );
};

export default WhatsAppConfig;

